import React, { useCallback } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  COLORS,
  Ledger as LedgerComponent,
  SectionHeader,
  theme,
  useNoticeBannersStackContext,
  VStack,
} from '@sweetgreen/garnish';

import { CheckoutLayoutBlock, LineItemCard } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useLedgerItems } from '../../../../../components/Bag/Bag.hooks';
import { useCheckoutContext } from '../../../CheckoutProvider';

// ─────────────────────────────────────────────────────────────────────────────

export const ConnectedCheckoutOrderBlock = () => {
  const context = useCheckoutContext();

  return <CheckoutOrderBlock {...context} />;
};

export const CheckoutOrderBlock = (
  props: Pick<
    ReturnType<typeof useCheckoutContext>,
    | 'payload'
    | 'orderId'
    | 'cart'
    | 'checkoutCredit'
    | 'isSubmittingOrder'
    | 'isCartPickup'
    | 'isCartDelivery'
    | 'cannotSubmitOrderReason'
    | 'shouldBlockCheckoutOnPress'
    | 'handleInvalidWantedTime'
    | 'validateCheckoutFields'
    | 'submitOrder'
    | 'ledger'
  >,
) => {
  const {
    payload,
    orderId,
    cart,
    ledger,
    isSubmittingOrder,
    isCartPickup,
    isCartDelivery,
    cannotSubmitOrderReason,
    shouldBlockCheckoutOnPress,
    handleInvalidWantedTime,
    validateCheckoutFields,
    submitOrder,
  } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const shouldApplyPickupTip = useFeatureFlag(
    'cels-754-enable-tipping-native-pickup',
  );

  const { lineItems = [] } = cart ?? {};

  // ───── Ledger ───────────────────────────────────────────────────

  const shouldShowTip =
    isCartDelivery || (shouldApplyPickupTip && isCartPickup);

  const ledgerItems = useLedgerItems({
    ledger,
    showTip: shouldShowTip,
    showTotal: true,
    showTaxes: true,
    showReward: true,
  });

  // ───── Submit Order ─────────────────────────────────────────────

  const submitOrderCallback = useCallback(async () => {
    validateCheckoutFields(payload);

    if (shouldBlockCheckoutOnPress || !orderId) {
      addNoticeBanner({ text: cannotSubmitOrderReason, palette: 'caution' });

      return;
    }

    await submitOrder({
      input: { ...payload, orderId },
      handleInvalidWantedTime,
    });
  }, [
    shouldBlockCheckoutOnPress,
    cannotSubmitOrderReason,
    orderId,
    payload,
    handleInvalidWantedTime,
    addNoticeBanner,
    validateCheckoutFields,
    submitOrder,
  ]);

  return (
    <CheckoutLayoutBlock>
      <SectionHeader heading={t('general.your-order')} />

      {/* ========== ========== ========== ========== */}

      <VStack customSpacing={0} divider={true} dividerColor={COLORS.LIGHT}>
        {lineItems.map((lineItem) => (
          <LineItemCard key={lineItem.id} lineItem={lineItem} />
        ))}
      </VStack>

      {/* ========== ========== ========== ========== */}

      <LedgerComponent
        style={styles.ledger}
        hasTopBorder
        items={ledgerItems}
        testID="checkout.ledger"
      />

      {/* ========== ========== ========== ========== */}

      <View style={styles.orderButtonWrapper}>
        <Button
          onPress={submitOrderCallback}
          isLoading={isSubmittingOrder}
          size="large"
          palette="primary"
          testID="checkout.place-order"
        >
          {t('checkout.place-order')}
        </Button>
      </View>

      {/* ========== ========== ========== ========== */}

      <VStack
        customSpacing={theme.spacing['4']}
        style={styles.helperTextsStack}
      >
        <CheckoutOrderBlockHelperText
          title={t('checkout.allergens-disclaimer')}
          text={t('checkout.allergens-disclaimer-message')}
        />
        {isCartDelivery ? (
          <CheckoutOrderBlockHelperText
            title={t('checkout.delivery-provider')}
            text={t('checkout.delivery-provider-message')}
          />
        ) : null}
        <CheckoutOrderBlockHelperText
          title={t('checkout.cancellation-policy')}
          text={t('checkout.cancellation-policy-message')}
        />
      </VStack>
    </CheckoutLayoutBlock>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type CheckoutOrderBlockInfoMessageProps = {
  readonly title?: string;
  readonly text: string;
} & Omit<ViewProps, 'children'>;

// ─── STYLES ──────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ledger: {
    marginTop: theme.spacing['2'],
  },
  orderButtonWrapper: {
    paddingTop: theme.spacing['1'],
  },
  helperTextsStack: {
    paddingTop: theme.spacing['10'],
  },
});

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

export const CheckoutOrderBlockHelperText = (
  props: CheckoutOrderBlockInfoMessageProps,
) => {
  const { title, text, ...restProps } = props;

  return (
    <View {...restProps}>
      {title ? (
        <BodyText size={5} bold>
          {title}
        </BodyText>
      ) : null}
      <BodyText size={5}>{text}</BodyText>
    </View>
  );
};
